import React, { type JSX } from 'react';
import { InfoListItem, ListItemTag } from '@brightlayer-ui/react-components';
import * as fileSaver from 'file-saver';
import { useTypedSelector } from '@fiji/common';
import { useGetDocumentsByFilterMutation } from '@fiji/common/src/features/documents/documentApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import {
    useDownloadMonthlyReportFileMutation,
    useGetAllMonthlyReportsQuery,
} from '@fiji/common/src/features/reports/reportApi';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    IconButton,
    List,
    Stack,
    Typography,
} from '@mui/material';
import moment from 'moment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterList from '@mui/icons-material/FilterList';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useDebounce, useIsMount } from 'hooks';
import { DocumentFilter } from './DocumentFilter';
import { ReportsFilter } from 'pages/Reports';
import { CustomTransComponent, Loader } from 'components';
import { Download } from '@mui/icons-material';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { DOCUMENTS_TEST_IDS, idOf, REPORTS_TEST_IDS } from '@fiji/common/src/testing/testId';
import { buildMonthlyReportRequest } from './monthlyReportRequest';
import { toSafeHttpUrl } from 'utils/security';
import { DocumentDownloadButton } from './DocumentDownloadButton';

export const DocumentReportList = ({
    orgId,
    groupId,
    embedded = false,
    emptyState,
}: {
    orgId?: string;
    groupId?: string;
    embedded?: boolean;
    emptyState?: React.ReactNode;
}): JSX.Element => {
    const { categoryId, type, typeId }: any = useParams();
    const { state } = useLocation();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const currentOrg = useTypedSelector(selectedOrg);
    const [searchKey, setSearchKey] = React.useState<string>();
    const [filterOpen, setFilterOpen] = React.useState(false);
    const [date, setDate] = React.useState<any>([null, null]);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const isMount = useIsMount();
    const [, debounceValue] = useDebounce(undefined, undefined, searchKey);
    const reportGroupId = groupId ?? orgId ?? (type === 'group' ? typeId : undefined);

    const [pagePayload, setPagePayload] = React.useState<any>({
        page: 0,
        size: 10,
    });
    const reportFilters = pagePayload?.filters ?? {};

    const {
        data: allReports,
        isFetching: reportsFetching,
        isLoading: reportsLoading,
        isSuccess: reportsLoaded,
    } = useGetAllMonthlyReportsQuery<any>(
        buildMonthlyReportRequest({
            deviceId: type === 'device' ? typeId : undefined,
            groupId: type !== 'device' ? reportGroupId : undefined,
            pagePayload,
            reportFilters,
        }),
        { skip: !currentOrg?.id, refetchOnMountOrArgChange: true }
    );
    const monthlyReports = React.useMemo(
        () => allReports?.records ?? allReports?.data?.records ?? [],
        [allReports?.records, allReports?.data?.records]
    );
    const [downloadMonthlyReportFile] = useDownloadMonthlyReportFileMutation();
    const activeDownloads = React.useRef(new Set<string>());
    const [downloadingReportIds, setDownloadingReportIds] = React.useState(new Set<string>());

    const handleDownloadReport = async (report: {
        fileId: string;
        fileName?: string | null;
        reportName?: string | null;
    }): Promise<void> => {
        if (activeDownloads.current.has(report.fileId)) return;

        activeDownloads.current.add(report.fileId);
        setDownloadingReportIds(new Set(activeDownloads.current));
        try {
            const reportFile = await downloadMonthlyReportFile(report.fileId).unwrap();
            fileSaver.saveAs(reportFile, report.fileName ?? `${report.reportName ?? 'report'}.pdf`);
        } catch {
            enqueueSnackbar(t('COMMON:DOWNLOAD_FAILED'), { variant: 'error' });
        } finally {
            activeDownloads.current.delete(report.fileId);
            setDownloadingReportIds(new Set(activeDownloads.current));
        }
    };

    const handlePreviewReport = async (report: any): Promise<void> => {
        const previewWindow = window.open('', '_blank');
        if (!previewWindow) return;

        const loadingLabel = t('COMMON:LOADING', { defaultValue: 'Loading...' });
        previewWindow.opener = null;
        previewWindow.document.open();
        previewWindow.document.write(`
            <!doctype html>
            <html>
                <head>
                    <title></title>
                    <style>
                        body { margin: 0; font-family: sans-serif; }
                        main { display: grid; min-height: 100vh; place-content: center; gap: 16px; color: #4b5563; }
                        .spinner { width: 36px; height: 36px; border: 4px solid #d9e2ec; border-top-color: #1683c4; border-radius: 50%; animation: spin 0.8s linear infinite; }
                        @keyframes spin { to { transform: rotate(360deg); } }
                    </style>
                </head>
                <body>
                    <main id="loading-status" role="status">
                        <div class="spinner" aria-hidden="true"></div>
                        <span id="loading-message"></span>
                    </main>
                </body>
            </html>
        `);
        previewWindow.document.close();
        previewWindow.document.title = loadingLabel;
        const loadingStatus = previewWindow.document.getElementById('loading-status');
        loadingStatus?.setAttribute('aria-label', loadingLabel);
        const loadingMessage = previewWindow.document.getElementById('loading-message');
        if (loadingMessage) loadingMessage.textContent = loadingLabel;
        try {
            const reportFile = await downloadMonthlyReportFile(report?.fileId).unwrap();
            const previewUrl = URL.createObjectURL(new Blob([reportFile], { type: 'application/pdf' }));
            previewWindow.location.href = previewUrl;

            const cleanupInterval = window.setInterval(() => {
                if (previewWindow.closed) {
                    URL.revokeObjectURL(previewUrl);
                    window.clearInterval(cleanupInterval);
                }
            }, 1000);
        } catch {
            previewWindow.close();
            enqueueSnackbar(t('COMMON:DOWNLOAD_FAILED'), { variant: 'error' });
        }
    };

    React.useEffect(() => {
        if (!isMount) {
            setPagePayload((prev: any) => ({
                ...prev,
                searchKey: debounceValue,
            }));
        }
    }, [debounceValue]);

    const [documentsData, setDocumentsData] = React.useState<any>({});

    const [getDocumentsByFilter] = useGetDocumentsByFilterMutation();

    const fetchDocuments = async (): Promise<void> => {
        const documentResponse: any = await getDocumentsByFilter({
            ...pagePayload,
            filters: {
                ...(type === 'device' && typeId && { deviceIds: [typeId] }),
                ...(type === 'group' && typeId !== currentOrg?.id && { groupIds: [typeId] }),
                documentTypes: [categoryId],
                ...pagePayload?.filters,
            },
        });
        const allDocumentsData: any = [];
        documentResponse?.data?.data?.records?.map((modelDocuments: any) => {
            modelDocuments.documentsData?.map((docData: any) => {
                allDocumentsData.push(docData?.documents);
            });
        });
        setDocumentsData((prev: any) => ({
            ...prev,
            [categoryId]: {
                data: allDocumentsData?.flat?.() ?? [],
                total: documentResponse?.data?.data?.total,
            },
        }));
        setIsLoadingMore(false);
    };

    const getCategoryIdContent = (): any => {
        if (categoryId) {
            return (
                <Accordion key={categoryId} defaultExpanded>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon color="primary" />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        className="border-bottom-1"
                    >
                        <Typography
                            variant="body2"
                            fontWeight={'600'}
                            fontSize={'14px'}
                            sx={{ color: theme.palette?.primary?.main }}
                        >
                            {state?.categoryName}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails className="padding-0">
                        <List disablePadding>
                            <>
                                {documentsData?.[categoryId]?.data?.map((document: any) => (
                                    <InfoListItem
                                        data-testid={DOCUMENTS_TEST_IDS.documentRow(
                                            idOf(document, 'fileId') ?? idOf(document, 'id') ?? idOf(document, 'name')
                                        )}
                                        onClick={(): void => {
                                            const safeUrl = toSafeHttpUrl(document?.url);
                                            if (safeUrl) {
                                                window.open(safeUrl, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                        key={document?.url}
                                        divider="partial"
                                        title={document?.displayName}
                                        subtitle={document?.name}
                                        chevron
                                        hidePadding
                                        leftComponent={
                                            <Stack>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontSize={'14px'}
                                                    fontWeight={'600'}
                                                    data-testid="document-month"
                                                >
                                                    {new Date(Number(document?.timestamp)).toLocaleString('default', {
                                                        month: 'short',
                                                    })}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontSize={'12px'}
                                                    letterSpacing={'1px'}
                                                    textTransform={'uppercase'}
                                                    data-testid="document-year"
                                                >
                                                    {new Date(Number(document?.timestamp))?.getFullYear()}
                                                </Typography>
                                            </Stack>
                                        }
                                        rightComponent={
                                            <>
                                                {document?.deleteSchedule && (
                                                    <Typography variant="body2" fontSize={'14px'}>
                                                        This file is scheduled for deletion on{' '}
                                                        {moment(new Date(document?.deleteSchedule)).format('LL')}
                                                    </Typography>
                                                )}
                                                <DocumentDownloadButton
                                                    testId={DOCUMENTS_TEST_IDS.downloadDocument(
                                                        idOf(document, 'fileId') ??
                                                            idOf(document, 'id') ??
                                                            idOf(document, 'name')
                                                    )}
                                                    url={document?.url}
                                                    fileName={document?.name}
                                                />
                                            </>
                                        }
                                    />
                                ))}
                                {(!documentsData?.[categoryId] || isLoadingMore) && (
                                    <Box sx={{ height: 50, position: 'relative' }}>
                                        <Loader size={36} />
                                    </Box>
                                )}
                                {Math.ceil(documentsData?.[categoryId]?.data?.length) <
                                    documentsData?.[categoryId]?.total && (
                                    <InfoListItem
                                        key="showMore"
                                        data-testid="infoListItem"
                                        sx={{ color: theme.palette?.primary?.main }}
                                        data-cy={'list-content'}
                                        title={
                                            <Typography color="primary" sx={{ fontWeight: 'bold' }}>
                                                {`View ${
                                                    documentsData?.[categoryId]?.total -
                                                    documentsData?.[categoryId]?.data?.length
                                                } More...`}
                                            </Typography>
                                        }
                                        hidePadding
                                        onClick={(): void => {
                                            setPagePayload((prev: any) => ({ ...prev, size: prev.size + 10 }));
                                            setIsLoadingMore(true);
                                        }}
                                    />
                                )}
                            </>
                        </List>
                    </AccordionDetails>
                </Accordion>
            );
        }
        return (
            <Accordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary" />}
                    aria-controls="reports-panel-content"
                    id="reports-panel-header"
                    className="border-bottom-1"
                >
                    <Typography variant="body2" fontWeight={'600'} fontSize={'14px'} color="primary">
                        <CustomTransComponent translationKey={'COMMON:MONTHLY_REPORTS'} />
                    </Typography>
                </AccordionSummary>
                <AccordionDetails className="padding-0">
                    <List disablePadding>
                        {monthlyReports.map((report: any) => (
                            <InfoListItem
                                data-testid={DOCUMENTS_TEST_IDS.reportRow(report?.fileId)}
                                onClick={(): void => void handlePreviewReport(report)}
                                key={report?.fileId}
                                divider="partial"
                                title={
                                    <Typography variant="body2" fontWeight={600}>
                                        {report?.reportName}
                                    </Typography>
                                }
                                subtitle={report?.fileName}
                                chevron
                                hidePadding
                                leftComponent={
                                    <Stack>
                                        <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'}>
                                            {report?.month
                                                ? moment().month(report.month).format('MMM').toUpperCase()
                                                : moment.unix(Number(report?.createdAt)).format('MMM').toUpperCase()}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontSize={'12px'}
                                            letterSpacing={'1px'}
                                            textTransform={'uppercase'}
                                        >
                                            {report?.year ?? moment.unix(Number(report?.createdAt)).format('YYYY')}
                                        </Typography>
                                    </Stack>
                                }
                                rightComponent={
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        {report?.isNew === true && (
                                            <ListItemTag
                                                data-testid={REPORTS_TEST_IDS.newTag(report?.fileId)}
                                                aria-label={t('COMMON:NEW_REPORT_TAG', { defaultValue: 'New' })}
                                                label={t('COMMON:NEW_REPORT_TAG', { defaultValue: 'New' })}
                                            />
                                        )}
                                        <IconButton
                                            data-testid={DOCUMENTS_TEST_IDS.downloadReport(report?.fileId)}
                                            aria-label={t('COMMON:DOWNLOAD')}
                                            disabled={downloadingReportIds.has(report.fileId)}
                                            aria-busy={downloadingReportIds.has(report.fileId)}
                                            onClick={(event): void => {
                                                event.stopPropagation();
                                                void handleDownloadReport(report);
                                            }}
                                        >
                                            {downloadingReportIds.has(report.fileId) ? (
                                                <CircularProgress
                                                    size={24}
                                                    aria-label={t('COMMON:LOADING', { defaultValue: 'Loading...' })}
                                                />
                                            ) : (
                                                <Download />
                                            )}
                                        </IconButton>
                                    </Stack>
                                }
                                // rightComponent={
                                //     document?.deleteSchedule ? (
                                //         <Typography variant="body2" fontSize={'14px'}>
                                //             This file is scheduled for deletion on{' '}
                                //             {moment(new Date(document?.deleteSchedule)).format('LL')}
                                //         </Typography>
                                //     ) : (
                                //         <></>
                                //     )
                                // }
                            />
                        ))}
                    </List>
                </AccordionDetails>
            </Accordion>
        );
    };

    React.useEffect(() => {
        void (async (): Promise<void> => {
            if (currentOrg?.id && categoryId) {
                await fetchDocuments();
            }
        })();
    }, [pagePayload, categoryId, currentOrg?.id]);
    return (
        <Stack p={embedded ? 0 : 2}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} py={1}>
                <InputWithCustomPlaceholder
                    id="searchKey"
                    className="margin-left-10"
                    onChange={(e: any): void => setSearchKey(e.target.value)}
                    inputProps={{ 'aria-label': 'search' }}
                    placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                />

                <IconButton id="filterList" onClick={(): void => setFilterOpen(!filterOpen)}>
                    <FilterList className="color-content" />
                </IconButton>
            </Stack>
            {filterOpen && categoryId && (
                <DocumentFilter
                    payload={pagePayload}
                    setPayload={setPagePayload}
                    customDate={date}
                    setCustomDate={setDate}
                    handleOpenFilter={(arg: boolean): void => setFilterOpen(arg)}
                />
            )}
            {filterOpen && !categoryId && (
                <ReportsFilter
                    payload={pagePayload}
                    setPayload={setPagePayload}
                    customDate={date}
                    setCustomDate={setDate}
                    handleOpenFilter={(arg: boolean): void => setFilterOpen(arg)}
                />
            )}
            {reportsFetching || reportsLoading ? (
                <Loader size={55} />
            ) : embedded && reportsLoaded && monthlyReports.length === 0 ? (
                emptyState ?? null
            ) : (
                getCategoryIdContent()
            )}
        </Stack>
    );
};
